import { useState, useCallback } from 'react';
import { trpc } from '../../lib/trpc';
import { MediaPickerModal } from './MediaPickerModal';
import type { Media } from '@wineryxplorer/shared';
import { Button } from '../ui/Button';
import { ImgWithSkeleton } from '../ui/ImgWithSkeleton';
import { FormSection } from '../ui/FormSection';

export interface WineMediaManagerProps {
  wineId: string;
  venueId: string;
  heroImageId?: string;
  galleryImageIds: string[];
  onHeroImageChange?: (heroImageId: string | null) => void;
  onGalleryImagesChange?: (galleryImageIds: string[]) => void;
  className?: string;
}

type PickerMode = 'hero' | 'gallery' | null;

export const WineMediaManager = ({
  wineId,
  venueId,
  heroImageId,
  galleryImageIds,
  onHeroImageChange,
  onGalleryImagesChange,
  className = '',
}: WineMediaManagerProps) => {
  const [activePickerMode, setActivePickerMode] = useState<PickerMode>(null);
  const [tempSelectedIds, setTempSelectedIds] = useState<string[]>([]);
  const utils = trpc.useContext();

  // Fetch media details for display
  const { data: heroMedia } = trpc.media.getById.useQuery(
    { id: heroImageId || '' },
    { enabled: !!heroImageId }
  );

  const { data: galleryMediaList } = trpc.media.getByIds.useQuery(
    { ids: galleryImageIds },
    { enabled: galleryImageIds.length > 0 }
  );

  // Mutation for updating wine media
  const updateWineMutation = trpc.wine.update.useMutation({
    onSuccess: () => {
      utils.wine.getById.invalidate({ id: wineId });
    },
    onError: () => {
      alert('Failed to save changes. Please try again.');
    },
  });

  // Handle temporary selection changes (while modal is open)
  const handleTempSelectionChange = useCallback((selectedIds: string[]) => {
    setTempSelectedIds(selectedIds);
  }, []);

  // Handle hero image selection confirmation (when Save is clicked)
  const handleHeroImageSelection = useCallback(() => {
    const heroId = tempSelectedIds.length > 0 ? tempSelectedIds[0] : null;
    
    // Always update wine directly for immediate persistence
    updateWineMutation.mutate({
      id: wineId,
      heroImageId: heroId || undefined,
    });
    
    // Also call callback if provided (for form state synchronization)
    if (onHeroImageChange) {
      onHeroImageChange(heroId);
    }
    
    setActivePickerMode(null);
    setTempSelectedIds([]);
  }, [tempSelectedIds, onHeroImageChange, updateWineMutation, wineId]);

  // Handle gallery images selection confirmation (when Save is clicked)
  const handleGalleryImagesSelection = useCallback(() => {
    // Always update wine directly for immediate persistence
    updateWineMutation.mutate({
      id: wineId,
      imageIds: tempSelectedIds,
    });
    
    // Also call callback if provided (for form state synchronization)
    if (onGalleryImagesChange) {
      onGalleryImagesChange(tempSelectedIds);
    }
    
    setActivePickerMode(null);
    setTempSelectedIds([]);
  }, [tempSelectedIds, onGalleryImagesChange, updateWineMutation, wineId]);

  // Handle picker close
  const handlePickerClose = useCallback(() => {
    setActivePickerMode(null);
    setTempSelectedIds([]);
  }, []);

  // Initialize temp selection when modal opens
  const handlePickerOpen = useCallback((mode: PickerMode) => {
    setActivePickerMode(mode);
    // Initialize temp selection with current values
    switch (mode) {
      case 'hero':
        setTempSelectedIds(heroImageId ? [heroImageId] : []);
        break;
      case 'gallery':
        setTempSelectedIds(galleryImageIds);
        break;
    }
  }, [heroImageId, galleryImageIds]);

  // Cast media lists to proper types
  const typedHeroMedia = heroMedia as Media | undefined;
  const typedGalleryMediaList = (galleryMediaList ?? []) as Media[];

  // Render media preview
  const renderMediaPreview = (media: Media | undefined, alt: string) => {
    if (!media) {
      return (
        <div className="w-24 h-24 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      );
    }

    return (
      <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
        <ImgWithSkeleton
          src={media.cloudinaryUrl}
          alt={alt}
          className="w-full h-full object-cover"
          skeletonClassName="rounded-lg"
        />
      </div>
    );
  };

  // Render media grid for multiple images
  const renderMediaGrid = (mediaList: Media[] | undefined, emptyText: string) => {
    if (!mediaList || mediaList.length === 0) {
      return (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-gray-500 text-sm">{emptyText}</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {mediaList.map((media) => (
          <div key={media.id} className="relative group">
            <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
              <ImgWithSkeleton
                src={media.cloudinaryUrl}
                alt={media.filename}
                className="w-full h-full object-cover"
                skeletonClassName="rounded-lg"
              />
            </div>
            
            <div className="mt-2">
              <p className="text-xs font-medium text-gray-900 truncate">
                {media.filename}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={className}>
      {/* Hero Image Section */}
      <FormSection
        title="Hero Image"
        titleAs="h3"
        description="Select a single image to represent your wine (bottle photo)."
        actions={
          <Button
            variant="primary"
            size="sm"
            onClick={() => handlePickerOpen('hero')}
          >
            {heroImageId ? 'Change Hero Image' : 'Select Hero Image'}
          </Button>
        }
      >
        <div className="flex items-center space-x-4">
          {renderMediaPreview(typedHeroMedia, 'Wine hero image')}
          
          {heroImageId && (
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">
                {typedHeroMedia?.filename || 'Loading...'}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Currently selected as wine hero image
              </p>
              <Button
                variant="link"
                size="sm"
                onClick={() => {
                  setTempSelectedIds([]);
                  handleHeroImageSelection();
                }}
                className="text-red-600 hover:text-red-700"
              >
                Remove hero image
              </Button>
            </div>
          )}
        </div>
      </FormSection>

      {/* Gallery Images Section */}
      <FormSection
        title="Gallery Images"
        titleAs="h3"
        description="Select multiple images for the wine gallery (label, vineyard, tasting notes, etc.)."
        actions={
          <Button
            variant="primary"
            size="sm"
            onClick={() => handlePickerOpen('gallery')}
          >
            {galleryImageIds.length > 0 ? 'Manage Gallery Images' : 'Select Gallery Images'}
          </Button>
        }
      >
        {renderMediaGrid(typedGalleryMediaList, 'No gallery images selected')}
        
        {galleryImageIds.length > 0 && (
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>{galleryImageIds.length} gallery images selected</span>
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                setTempSelectedIds([]);
                handleGalleryImagesSelection();
              }}
              className="text-red-600 hover:text-red-700"
            >
              Clear all
            </Button>
          </div>
        )}
      </FormSection>

      {/* Media Picker Modals */}
      <MediaPickerModal
        isOpen={activePickerMode === 'hero'}
        onClose={handlePickerClose}
        mode="single"
        venueId={venueId}
        selectedIds={tempSelectedIds}
        onSelectionChange={handleTempSelectionChange}
        onSave={handleHeroImageSelection}
        title="Select Wine Hero Image"
        maxSelection={1}
        isSaving={updateWineMutation.isPending}
      />

      <MediaPickerModal
        isOpen={activePickerMode === 'gallery'}
        onClose={handlePickerClose}
        mode="multi"
        venueId={venueId}
        selectedIds={tempSelectedIds}
        onSelectionChange={handleTempSelectionChange}
        onSave={handleGalleryImagesSelection}
        title="Select Wine Gallery Images"
        maxSelection={50}
        isSaving={updateWineMutation.isPending}
      />
    </div>
  );
};
